import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FunnelShell } from "./components/FunnelShell";
import { StepRenderer, preloadStepRendererChunk } from "./components/StepRenderer";
import { BottomConfirmBar } from "./components/ui/BottomConfirmBar";
import { TopBar } from "./components/ui/TopBar";
import { getImagePreloadCandidatesForSources } from "./components/imageMeta";
import { computeSectionProgress, getFlowSteps, getFunnelSections } from "./funnel/flowData";
import { resolveFlowFromUrl } from "./funnel/resolveFlowFromUrl";
import { replaceStepInUrl, resolveStepFromUrl } from "./funnel/resolveStepFromUrl";
import { useFunnelState } from "./funnel/useFunnelState";
import { buildFunnelImageRepository } from "./perf/funnelImageRepository";
import { startPhasedImagePreloadOnce } from "./perf/preloadImages";
import { getCheckoutImagePreloadUrls } from "./perf/checkoutImageAssets";

const pageVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 32 : -32,
    opacity: 0,
    filter: "blur(6px)"
  }),
  center: {
    x: 0,
    opacity: 1,
    filter: "blur(0px)"
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -32 : 32,
    opacity: 0,
    filter: "blur(6px)"
  })
};

const pageTransition = {
  duration: 0.32,
  ease: [0.22, 1, 0.36, 1]
} as const;

type IdleDeadlineLike = {
  didTimeout: boolean;
  timeRemaining: () => number;
};

type ScheduledTaskHandle =
  | { type: "idle"; id: number }
  | { type: "timeout"; id: number };

type WindowWithStartupFlags = Window &
  typeof globalThis & {
    __LUMI_WELCOME_SHELL_CONTINUE__?: boolean;
    __LUMI_WELCOME_SHELL_CAROUSEL_TIMER__?: number;
    requestIdleCallback?: (callback: (deadline: IdleDeadlineLike) => void, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

const scheduleIdleTask = (work: () => void, timeout = 1500): ScheduledTaskHandle => {
  const idleWindow = window as WindowWithStartupFlags;

  if (typeof idleWindow.requestIdleCallback === "function") {
    return {
      type: "idle",
      id: idleWindow.requestIdleCallback(
        (deadline) => {
          if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
            work();
            return;
          }

          work();
        },
        { timeout }
      )
    };
  }

  return {
    type: "timeout",
    id: window.setTimeout(work, timeout)
  };
};

const cancelScheduledTask = (task: ScheduledTaskHandle | null): void => {
  if (!task) {
    return;
  }

  if (task.type === "idle") {
    (window as WindowWithStartupFlags).cancelIdleCallback?.(task.id);
    return;
  }

  window.clearTimeout(task.id);
};

const preloadLateStepModules = (): Promise<unknown[]> => {
  return Promise.all([
    import("./api/checkout"),
    import("./api/leads"),
    import("./funnel/buildOnboardingResponses"),
    import("./analytics/metaPixel")
  ]);
};

const App = () => {
  const flow = useMemo(() => resolveFlowFromUrl(), []);
  const steps = useMemo(() => getFlowSteps(flow), [flow]);
  const funnelSections = useMemo(() => getFunnelSections(steps), [steps]);
  const initialStepId = useMemo(() => resolveStepFromUrl(), []);
  const {
    answers,
    currentIndex,
    currentStep,
    direction,
    canGoBack,
    canContinue,
    goBack,
    goNext,
    goToIndex,
    setSingleAnswer,
    toggleMultiAnswer,
    setTextAnswer
  } = useFunnelState(steps, { flowKey: flow, initialStepId });
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [, setIsWelcomeHeroReady] = useState(true);
  const emailSubmittedRef = useRef(false);
  const backgroundWarmupStartedRef = useRef(false);
  const isWelcomeStep = currentStep?.id === "welcome";
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("body-welcome-step", isWelcomeStep);

    return () => {
      document.body.classList.remove("body-welcome-step");
    };
  }, [isWelcomeStep]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const welcomeStep = steps[0];
      if (welcomeStep?.kind === "welcome") {
        startPhasedImagePreloadOnce({
          runKey: `flow:${flow}:welcome-hero`,
          critical: getImagePreloadCandidatesForSources(
            welcomeStep.heroSlides?.map((slide) => slide.image)
          ),
          secondary: [],
          concurrency: 1
        });

        const validationWarnings = buildFunnelImageRepository({
          steps,
          currentIndex: 0,
          lookaheadSteps: 0
        }).warnings;

        if (import.meta.env.DEV && validationWarnings.length > 0) {
          console.warn("[image-preload] validation warnings", validationWarnings);
        }
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [flow, steps]);

  useEffect(() => {
    if (!currentStep || currentStep.kind === "welcome") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const imageRepository = buildFunnelImageRepository({
        steps,
        currentIndex,
        lookaheadSteps: 1
      });

      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:step:${currentStep.id}`,
        critical: imageRepository.critical,
        secondary: imageRepository.secondary,
        concurrency: 2
      });

      if (import.meta.env.DEV && imageRepository.warnings.length > 0) {
        console.warn("[image-preload] validation warnings", imageRepository.warnings);
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, currentStep, flow, steps]);

  const checkoutPreloadTriggerIndex = useMemo(() => {
    const paymentStepIndex = steps.findIndex((step) => step.kind === "payment");
    if (paymentStepIndex < 0) {
      return -1;
    }
    return Math.max(paymentStepIndex - 2, 0);
  }, [steps]);

  useEffect(() => {
    if (checkoutPreloadTriggerIndex < 0 || currentIndex < checkoutPreloadTriggerIndex) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:checkout-images`,
        critical: getCheckoutImagePreloadUrls(),
        secondary: []
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [checkoutPreloadTriggerIndex, currentIndex, flow]);

  useEffect(() => {
    const startupWindow = window as WindowWithStartupFlags;
    if (startupWindow.__LUMI_WELCOME_SHELL_CAROUSEL_TIMER__) {
      window.clearInterval(startupWindow.__LUMI_WELCOME_SHELL_CAROUSEL_TIMER__);
      startupWindow.__LUMI_WELCOME_SHELL_CAROUSEL_TIMER__ = undefined;
    }

    document.documentElement.dataset.appReady = "true";
  }, []);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome") {
      return;
    }

    const startupWindow = window as WindowWithStartupFlags;
    if (!startupWindow.__LUMI_WELCOME_SHELL_CONTINUE__) {
      return;
    }

    startupWindow.__LUMI_WELCOME_SHELL_CONTINUE__ = false;
    const continueTimer = window.setTimeout(() => {
      goNext();
    }, 0);

    return () => {
      window.clearTimeout(continueTimer);
    };
  }, [currentStep, goNext]);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome" || backgroundWarmupStartedRef.current) {
      return;
    }

    backgroundWarmupStartedRef.current = true;

    const warmupStartIndex = Math.min(currentIndex + 1, steps.length - 1);
    const postWelcomeImageRepository = buildFunnelImageRepository({
      steps,
      currentIndex: warmupStartIndex,
      lookaheadSteps: 1
    });
    let secondWarmupTask: ScheduledTaskHandle | null = null;

    const firstWarmupTask = scheduleIdleTask(() => {
      void preloadStepRendererChunk(steps[currentIndex + 1]?.kind);

      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:next-step-images`,
        critical: postWelcomeImageRepository.critical,
        secondary: [],
        concurrency: 1
      });

      secondWarmupTask = scheduleIdleTask(() => {
        const stepKinds = Array.from(new Set(steps.slice(currentIndex + 1).map((step) => step.kind)));

        void Promise.all([
          preloadLateStepModules(),
          ...stepKinds.map((kind) => preloadStepRendererChunk(kind))
        ]);

        startPhasedImagePreloadOnce({
          runKey: `flow:${flow}:background-secondary`,
          critical: [],
          secondary: postWelcomeImageRepository.secondary,
          concurrency: 2
        });
      }, 2500);
    }, 1200);

    return () => {
      cancelScheduledTask(firstWarmupTask);
      cancelScheduledTask(secondWarmupTask);
    };
  }, [currentIndex, currentStep, flow, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    if (currentStep.kind !== "welcome") {
      setIsWelcomeHeroReady(true);
      return;
    }

    setIsWelcomeHeroReady(!currentStep.heroImage);
  }, [currentStep]);

  useEffect(() => {
    const nextStep = steps[currentIndex + 1];
    if (!nextStep) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      void preloadStepRendererChunk(nextStep.kind);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }
    replaceStepInUrl(currentStep.id);
  }, [currentStep]);

  useEffect(() => {
    const onBrowserNavigation = () => {
      const stepIdFromUrl = resolveStepFromUrl();
      if (!stepIdFromUrl) {
        return;
      }

      const nextIndex = steps.findIndex((step) => step.id === stepIdFromUrl);
      if (nextIndex >= 0) {
        goToIndex(nextIndex);
      }
    };

    window.addEventListener("popstate", onBrowserNavigation);
    window.addEventListener("hashchange", onBrowserNavigation);
    return () => {
      window.removeEventListener("popstate", onBrowserNavigation);
      window.removeEventListener("hashchange", onBrowserNavigation);
    };
  }, [goToIndex, steps]);

  if (!currentStep) {
    return null;
  }

  const sectionProgress = computeSectionProgress(currentIndex, funnelSections);
  const isPaymentStep = currentStep.kind === "payment";
  const isLoadingStep = currentStep.kind === "loading";
  const isCheckoutCompleteStep = currentStep.id === "checkout_complete";
  const shouldShowBottomBar = !isLoadingStep && !isPaymentStep && !isCheckoutCompleteStep;
  const confirmLabelText = currentStep.confirmLabel ?? "Continue →";
  const welcomeFinePrint = currentStep.kind === "welcome" ? currentStep.finePrint : undefined;
  
  const confirmLabel = confirmLabelText;
  const confirmDisabled = !canContinue || isSubmittingEmail || isStartingCheckout;

  const handleConfirm = async () => {
    if (!canContinue || isSubmittingEmail || isStartingCheckout) {
      return;
    }

    if (currentStep.kind === "payment") {
      const selectedPlanId = answers[currentStep.answerKey];
      if (typeof selectedPlanId !== "string") {
        return;
      }

      const selectedPlan = currentStep.plans.find((plan) => plan.id === selectedPlanId);
      if (!selectedPlan) {
        return;
      }

      const [{ createCheckoutSession }, { getMetaTrackingContext, rememberPendingCheckoutTracking }] = await Promise.all([
        import("./api/checkout"),
        import("./analytics/metaPixel")
      ]);
      const trackingContext = getMetaTrackingContext();

      const emailStepEnabled = steps.some(
        (step) => step.kind === "email" && step.answerKey === "email"
      );
      const email = emailStepEnabled && typeof answers.email === "string" ? answers.email : "";
      const planValue = parseFloat(selectedPlan.totalPrice.replace(/[^0-9.]/g, "")) || 0;

      setIsStartingCheckout(true);
      try {
        const session = await createCheckoutSession({
          ...(email ? { email } : {}),
          planId: selectedPlan.id,
          flow,
          value: planValue,
          eventId: trackingContext.eventId,
          ...(trackingContext.fbp ? { fbp: trackingContext.fbp } : {}),
          ...(trackingContext.fbc ? { fbc: trackingContext.fbc } : {})
        });

        rememberPendingCheckoutTracking({
          sessionId: session.sessionId,
          eventId: trackingContext.eventId,
          planId: selectedPlan.id,
          value: planValue,
          currency: "USD"
        });

        window.location.assign(session.checkoutUrl);
      } catch (error) {
        console.error("[checkout] failed to create Stripe checkout session", error);
      } finally {
        setIsStartingCheckout(false);
      }

      return;
    }

    if (currentStep.kind === "email") {
      if (emailSubmittedRef.current) {
        goNext();
        return;
      }
      const email = answers[currentStep.answerKey] as string;
      const [{ buildOnboardingResponses }, { saveLead }] = await Promise.all([
        import("./funnel/buildOnboardingResponses"),
        import("./api/leads")
      ]);
      const onboardingResponses = buildOnboardingResponses(steps, answers);
      emailSubmittedRef.current = true;
      setIsSubmittingEmail(true);
      try {
        await saveLead(email, onboardingResponses);
      } finally {
        setIsSubmittingEmail(false);
      }
      goNext();
      return;
    }

    goNext();
  };

  const handleSelectSingle = (answerKey: string, optionId: string) => {
    setSingleAnswer(answerKey, optionId);
    const shouldAutoAdvance =
      currentStep.kind === "question" && !currentStep.optionFeedback;
    if (shouldAutoAdvance) {
      setTimeout(goNext, 100);
    }
  };

  return (
    <div className={`app-frame ${isWelcomeStep ? "app-frame-welcome" : ""}`}>
      <FunnelShell
        isScrollable={isPaymentStep}
        topBar={
          isPaymentStep ? null : (
            <TopBar
              canGoBack={canGoBack && currentStep.id !== "checkout_complete"}
              onBack={goBack}
              sections={funnelSections}
              sectionProgress={sectionProgress}
              isWelcomeStep={isWelcomeStep}
              useWelcomeChrome
            />
          )
        }
        bottomBar={
          shouldShowBottomBar ? (
            <BottomConfirmBar
              label={confirmLabel}
              disabled={confirmDisabled}
              onClick={handleConfirm}
              note={welcomeFinePrint}
              className={isWelcomeStep ? "bottom-confirm-bar-welcome" : undefined}
            />
          ) : undefined
        }
      >
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={currentStep.id}
            className={`step-page ${isWelcomeStep ? "step-page-welcome" : ""}`}
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={pageTransition}
          >
            <StepRenderer
              step={currentStep}
              answers={answers}
              onSelectSingle={handleSelectSingle}
              onToggleMulti={toggleMultiAnswer}
              onSetText={setTextAnswer}
              onWelcomeFirstImageReadyChange={setIsWelcomeHeroReady}
              onLoadingContinue={goNext}
              onPaymentContinue={handleConfirm}
              isPaymentSubmitting={isStartingCheckout}
            />
          </motion.div>
        </AnimatePresence>
      </FunnelShell>
    </div>
  );
};

export default App;
